import React, { useState, useCallback } from "react"
import { Navigate, useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import useIdleTimeout from "../hooks/useIdleTimeout"
import SessionTimeoutModal from "../components/modal/SessionTimeoutModal"

const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { isAuth, onLoading, onLogout, onLogoutDueToTimeout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [showWarning, setShowWarning] = useState(false)

  // Handle session timeout - logout and redirect with reason
  const handleTimeout = useCallback(async () => {
    setShowWarning(false)
    await onLogoutDueToTimeout()
  }, [onLogoutDueToTimeout])

  // Handle warning - show modal
  const handleWarning = useCallback(() => {
    setShowWarning(true)
  }, [])

  // Use idle timeout hook (only enabled when authenticated)
  const { resetTimer, remainingTime } = useIdleTimeout({
    onTimeout: handleTimeout,
    onWarning: handleWarning,
    enabled: isAuth,
  })

  // Handle continue session - reset timer and close modal
  const handleContinue = useCallback(() => {
    setShowWarning(false)
    resetTimer()
  }, [resetTimer])

  // Handle logout now - immediate logout
  const handleLogoutNow = useCallback(async () => {
    setShowWarning(false)
    await handleTimeout()
  }, [handleTimeout])

  if (onLoading) {
    // Show loading indicator
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  // If user is not authenticated, redirect to login
  if (!isAuth) {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get("reason");
    const to = reason ? `/?reason=${reason}` : "/";
    return <Navigate to={to} state={{ from: location }} replace />
  }

  // User is authenticated and has required role, render the component with timeout modal
  return (
    <>
      {children}
      <SessionTimeoutModal
        isOpen={showWarning}
        remainingTime={remainingTime}
        onContinue={handleContinue}
        onLogout={handleLogoutNow}
      />
    </>
  )
}

export default ProtectedRoute
